import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Alert,
  Checkbox,
  FormControlLabel,
  Divider
} from '@mui/material';
import { Save, User, Download, Upload, Utensils, LogOut, Settings as SettingsIcon } from 'lucide-react';
import { DatabaseSchema, UserPreferences } from '../types';
import { importDatabase } from '../utils/fileStorage';
import { useToast } from './shared/ToastProvider';

interface SettingsProps {
  database: DatabaseSchema | null;
  onUpdatePreferences: (preferences: UserPreferences) => Promise<void>;
  onRefreshDatabase: () => Promise<void>;
  onLogout?: () => void;
}

export const Settings: React.FC<SettingsProps> = ({ database, onUpdatePreferences, onRefreshDatabase, onLogout }) => {
  const toast = useToast();
  const [preferences, setPreferences] = useState<UserPreferences>({
    height: 169,
    initialWeight: 144.2,
    goalWeight: 99.9,
    monthlyWeightLossTarget: 4.0,
    startDate: '2025-08-22',
    units: {
      weight: 'kg',
      height: 'cm'
    }
  });

  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    if (database?.preferences) {
      setPreferences(database.preferences);
    }
  }, [database]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      await onUpdatePreferences(preferences);
      toast.success('Settings saved successfully!');
    } catch (error) {
      console.error('Error updating preferences:', error);
      toast.error('Failed to save settings.');
    } finally {
      setLoading(false);
    }
  };

  const handleHeightChange = (value: string) => {
    const height = parseFloat(value);
    if (!isNaN(height) && height > 0) {
      setPreferences(prev => ({ ...prev, height }));
    }
  };

  const handleInitialWeightChange = (value: string) => {
    const weight = parseFloat(value);
    if (!isNaN(weight) && weight > 0) {
      setPreferences(prev => ({ ...prev, initialWeight: weight }));
    }
  };

  const handleGoalWeightChange = (value: string) => {
    const weight = parseFloat(value);
    if (!isNaN(weight) && weight > 0) {
      setPreferences(prev => ({ ...prev, goalWeight: weight }));
    }
  };

  const handleMonthlyTargetChange = (value: string) => {
    const target = parseFloat(value);
    if (!isNaN(target) && target > 0) {
      setPreferences(prev => ({ ...prev, monthlyWeightLossTarget: target }));
    }
  };

  const handleStartDateChange = (value: string) => {
    setPreferences(prev => ({ ...prev, startDate: value }));
  };

  const handleUnitChange = (unit: keyof UserPreferences['units'], value: string) => {
    setPreferences(prev => ({
      ...prev,
      units: { ...prev.units, [unit]: value }
    }));
  };

  const handleExportDatabase = async () => {
    if (!database) return;

    try {
      const dataStr = JSON.stringify(database, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      const now = new Date();
      const dateStr = now.toISOString().split('T')[0];
      link.download = `stride-weight-tracker-export-${dateStr}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Database exported successfully!');
    } catch (error) {
      console.error('Error exporting database:', error);
      toast.error('Failed to export database.');
    }
  };

  const handleImportDatabase = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImporting(true);

    try {
      const fileContent = await file.text();
      const parsedData = JSON.parse(fileContent);

      if (typeof parsedData !== 'object' || !parsedData.weights || !parsedData.goals) {
        throw new Error('Invalid database file format');
      }

      await importDatabase(fileContent);
      await onRefreshDatabase();
      toast.success('Database imported successfully!');
    } catch (error) {
      console.error('Error importing database:', error);
      toast.error(error instanceof Error ? error.message : 'Failed to import database');
    } finally {
      setImporting(false);
      event.target.value = '';
    }
  };

  const SectionHeader = ({ icon, title }: { icon: React.ReactNode; title: string }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={2.5}>
      <Box sx={{
        width: 36, height: 36, borderRadius: '10px',
        backgroundColor: '#eef2ff', color: '#6366f1',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 600 }}>{title}</Typography>
    </Box>
  );

  return (
    <Box>
      <Box mb={3}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>Settings</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          Manage your profile, goals, and preferences
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Grid container spacing={2.5}>
          {/* Profile */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SectionHeader icon={<User size={18} />} title="Profile" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Height (cm)" type="number" value={preferences.height}
                      onChange={(e) => handleHeightChange(e.target.value)}
                      inputProps={{ step: "0.1", min: "100", max: "250" }}
                      InputLabelProps={{ shrink: true }}
                      helperText={`${(preferences.height / 100).toFixed(2)}m`}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Initial Weight (kg)" type="number" value={preferences.initialWeight}
                      onChange={(e) => handleInitialWeightChange(e.target.value)}
                      inputProps={{ step: "0.1", min: "30", max: "300" }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Your starting weight"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Start Date" type="date" value={preferences.startDate}
                      onChange={(e) => handleStartDateChange(e.target.value)}
                      InputLabelProps={{ shrink: true }}
                      helperText="Journey start date"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Goals */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SectionHeader icon={<SettingsIcon size={18} />} title="Goals & Targets" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Goal Weight (kg)" type="number" value={preferences.goalWeight}
                      onChange={(e) => handleGoalWeightChange(e.target.value)}
                      inputProps={{ step: "0.1", min: "30", max: "300" }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Your target weight"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField fullWidth label="Monthly Target (kg/month)" type="number" value={preferences.monthlyWeightLossTarget}
                      onChange={(e) => handleMonthlyTargetChange(e.target.value)}
                      inputProps={{ step: "0.1", min: "0.5", max: "10" }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Target loss per month"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Nutrition Plan */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SectionHeader icon={<Utensils size={18} />} title="Nutrition Plan" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Plan Start Date" type="date"
                      value={preferences.nutritionPhaseStartDate || ''}
                      onChange={(e) => setPreferences(prev => ({ ...prev, nutritionPhaseStartDate: e.target.value }))}
                      InputLabelProps={{ shrink: true }}
                      helperText="For phase calculation"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Daily Protein Target (g)" type="number"
                      value={preferences.dailyProteinTarget || ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setPreferences(prev => ({ ...prev, dailyProteinTarget: isNaN(val) ? undefined : val }));
                      }}
                      inputProps={{ min: 60, max: 300, step: 5 }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Leave blank for auto"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Weekday Calories" type="number"
                      value={preferences.weekdayCalorieTarget || ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setPreferences(prev => ({ ...prev, weekdayCalorieTarget: isNaN(val) ? undefined : val }));
                      }}
                      inputProps={{ min: 1200, max: 4000, step: 50 }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Override phase default"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <TextField fullWidth label="Weekend Calories" type="number"
                      value={preferences.weekendCalorieTarget || ''}
                      onChange={(e) => {
                        const val = parseFloat(e.target.value);
                        setPreferences(prev => ({ ...prev, weekendCalorieTarget: isNaN(val) ? undefined : val }));
                      }}
                      inputProps={{ min: 1200, max: 4000, step: 50 }}
                      InputLabelProps={{ shrink: true }}
                      helperText="Override phase default"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Health & activity */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <SectionHeader icon={<SettingsIcon size={18} />} title="Health & activity" />
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Weekly activity target (minutes)"
                      type="number"
                      value={preferences.weeklyActivityMinutesTarget ?? 150}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setPreferences((prev) => ({ ...prev, weeklyActivityMinutesTarget: isNaN(v) ? 150 : Math.max(0, Math.min(420, v)) }));
                      }}
                      inputProps={{ min: 0, max: 420, step: 15 }}
                      InputLabelProps={{ shrink: true }}
                      helperText="NHS guideline: 150 min/wk moderate activity"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Target style</InputLabel>
                      <Select
                        value={preferences.targetMode ?? 'weekly_minutes'}
                        label="Target style"
                        onChange={(e) => setPreferences((prev) => ({ ...prev, targetMode: e.target.value as 'weekly_minutes' | 'daily_habit' }))}
                      >
                        <MenuItem value="weekly_minutes">Weekly minutes ring</MenuItem>
                        <MenuItem value="daily_habit">Daily habit (walked today y/n)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="BP target — systolic"
                      type="number"
                      value={preferences.bpTarget?.systolic ?? 135}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setPreferences((prev) => ({
                          ...prev,
                          bpTarget: { systolic: isNaN(v) ? 135 : v, diastolic: prev.bpTarget?.diastolic ?? 85 },
                        }));
                      }}
                      inputProps={{ min: 80, max: 200 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="BP target — diastolic"
                      type="number"
                      value={preferences.bpTarget?.diastolic ?? 85}
                      onChange={(e) => {
                        const v = parseInt(e.target.value, 10);
                        setPreferences((prev) => ({
                          ...prev,
                          bpTarget: { diastolic: isNaN(v) ? 85 : v, systolic: prev.bpTarget?.systolic ?? 135 },
                        }));
                      }}
                      inputProps={{ min: 50, max: 120 }}
                      InputLabelProps={{ shrink: true }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={preferences.showGym ?? false}
                          onChange={(e) => setPreferences((prev) => ({ ...prev, showGym: e.target.checked }))}
                        />
                      }
                      label="Show gym / strength-training section (off by default — walks are the main focus)"
                    />
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Units */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2.5 }}>Unit Preferences</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Weight Unit</InputLabel>
                      <Select value={preferences.units.weight} label="Weight Unit"
                        onChange={(e) => handleUnitChange('weight', e.target.value)}>
                        <MenuItem value="kg">Kilograms (kg)</MenuItem>
                        <MenuItem value="lbs">Pounds (lbs)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth>
                      <InputLabel>Height Unit</InputLabel>
                      <Select value={preferences.units.height} label="Height Unit"
                        onChange={(e) => handleUnitChange('height', e.target.value)}>
                        <MenuItem value="cm">Centimeters (cm)</MenuItem>
                        <MenuItem value="ft">Feet/Inches (ft)</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Data Management */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" sx={{ fontWeight: 600, mb: 2.5 }}>Data Management</Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Export your complete database as a JSON file for backup.
                    </Typography>
                    <Button fullWidth variant="outlined" onClick={handleExportDatabase} startIcon={<Download size={18} />} sx={{ py: 1.5 }}>
                      Export Database
                    </Button>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary" gutterBottom>
                      Import a previously exported JSON file to restore data.
                    </Typography>
                    <input accept=".json" style={{ display: 'none' }} id="import-file-input" type="file" onChange={handleImportDatabase} disabled={importing} />
                    <label htmlFor="import-file-input">
                      <Button fullWidth variant="outlined" component="span" disabled={importing} startIcon={<Upload size={18} />} sx={{ py: 1.5 }}>
                        {importing ? 'Importing...' : 'Import Database'}
                      </Button>
                    </label>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Save Button */}
          <Grid item xs={12}>
            <Button fullWidth variant="contained" type="submit" disabled={loading} startIcon={<Save size={18} />} size="large">
              {loading ? 'Saving...' : 'Save Settings'}
            </Button>
          </Grid>

          {/* Logout */}
          {onLogout && (
            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
              <Card sx={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca' }}>
                <CardContent sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Box>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#991b1b' }}>Sign Out</Typography>
                    <Typography variant="caption" color="text.secondary">End your current session</Typography>
                  </Box>
                  <Button variant="outlined" color="error" startIcon={<LogOut size={16} />} onClick={onLogout}>
                    Logout
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* About */}
          <Grid item xs={12}>
            <Box textAlign="center" py={2}>
              <Typography variant="caption" color="text.secondary">
                Stride Weight Tracker v1.0 &middot; &copy; {new Date().getFullYear()}
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </form>
    </Box>
  );
};
